"use client";

import { useEffect, useState } from "react";

import { useKeyboardInsets } from "@hooks/useKeyboardInsets";

/**
 * 软键盘遮挡底部的高度（px），供底部弹层把内容整体上移，保证 Apply 始终可点。
 *
 * - iOS 壳（键盘 inset bridge 可用）：取原生上报的键盘高度；`native-resize` 模式下
 *   WebView 已自行缩高，返回 0 以免重复让位
 * - 浏览器 / 其他壳：`visualViewport` 比布局视口矮出来的部分即为键盘遮挡
 */
export function useKeyboardInsetBottom(enabled: boolean): number {
  const keyboardInsets = useKeyboardInsets();
  const [viewportInset, setViewportInset] = useState(0);

  useEffect(() => {
    const viewport =
      typeof window === "undefined" ? undefined : window.visualViewport;
    if (!enabled || keyboardInsets.bridgeEnabled || !viewport) {
      setViewportInset(0);
      return;
    }
    const update = () => {
      setViewportInset(
        Math.max(
          0,
          Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
        ),
      );
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [enabled, keyboardInsets.bridgeEnabled]);

  if (!enabled) {
    return 0;
  }
  if (keyboardInsets.bridgeEnabled) {
    return keyboardInsets.visible && keyboardInsets.shouldApplyWebInsetPadding
      ? keyboardInsets.height
      : 0;
  }
  return viewportInset;
}
